import { DataGridPro, DataGridProProps, GridActionsColDef, gridClasses, GridColDef, GridColType, GridColumnResizeParams, GridRenderCellParams, GridSingleSelectColDef, GridValidRowModel, useGridApiRef } from '@mui/x-data-grid-pro';



import Box from '@mui/material/Box';
import { hasErrors } from 'common/enums';
import { BaseModel } from 'common/models/base';
import { appStore } from 'common/store/store';
import localStore from 'front/locaStorage';
import React from 'react';
import { postData } from '../api';
import { CopyButton } from '../libraries/CopyButton';

export type SimGridBaseColDef<R extends GridValidRowModel = any, V = any, F = V> = GridColDef<R, V, F> & {
    type?: GridColType | 'editabletext';
    canCopy?: boolean;
};


const EditableTextRenderCell = (props: GridRenderCellParams) => {
    return <Box sx={{ position: 'relative', width: '100%' }}>
        <Box component='span' className='editabletext-content' sx={{ overflow: 'ellipsis' }} onClick={() => {
            props.api.startCellEditMode({
                field: props.field,
                id: props.id
            });
        }}>{props.value}</Box>
        {<Box className='copyboxretirer' sx={{ display: 'inline', position: 'absolute', right: 0 }}>
            <CopyButton contentToCopy={props.value} />
        </Box>}
    </Box>;
};


const GenerateEditableColDef = (p: GridColDef): GridColDef => {
    p.cellClassName = 'editabletext';
    p.renderCell = EditableTextRenderCell;
    return p;
};


export type SimGridColType = GridColType | 'editabletext';

export type SimGridColDef<R extends GridValidRowModel = any, V = any, F = V> = SimGridBaseColDef<R, V, F> | GridActionsColDef<R, V, F> | GridSingleSelectColDef<R, V, F>;

export default <T extends BaseModel,>(props: DataGridProProps<T> & {
    gridId: string,
    rows: T[],
    modelName: string;
    slice: AdapterSlice;
    onRowUpdated?: (updated: T, original: T) => void;
    columns: SimGridColDef[];
}) => {

    const apiRef = useGridApiRef();
    const [config, setConfig] = localStore.useLocalStorage(props.gridId, {});


    const saveConfig = async (newConfig: any) => {
        setConfig(newConfig);
    };

    const updateRow = async (updateRow: T, originalRow: T) => {
        const response = await postData<T>(props.modelName, updateRow);
        if (hasErrors(response)) {

        } else {
            appStore.dispatch(props.slice.actions.upsertOne(response));
            apiRef.current.updateRows([response]);
            if (props.onRowUpdated) {
                props.onRowUpdated(response, originalRow);
            }
        }
    };

    const simColumns = React.useMemo(() => {
        return props.columns.map(p => {
            p.width = config?.[p.field]?.width ?? p.width ?? null;
            switch ((p.type as any)) {
                case 'editabletext':
                    return GenerateEditableColDef(p);
                default:
                    return p;
            }
        });
    }, [props.columns]);

    const [width, setWidth] = React.useState(1000);

    React.useEffect(() => {
        resizeGrid();
    }, props.columns);

    const resizeGrid = (() => {
        const w = props.columns.reduce((acc, curr: any) => { return acc + (curr?.width ?? 100); }, 150);
        setWidth(w > 1000 ? w : 1000);
    });

    return <div style={{ width: width }}><DataGridPro
        {...props}
        columns={simColumns}
        apiRef={apiRef}
        sx={{
            [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
                outline: 'none',
            },
            [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
            {
                outline: 'none',
            },
            '& .editabletext-content, & .editabletext input': {
                margin: '0 2px 0 0',
                padding: '3px 6px',
                borderWidth: '1px',
                borderRadius: '3px',
                borderStyle: 'none',
                height: 'auto'
            },
            '& .editabletext-content:hover': {
                borderStyle: 'solid',
                borderColor: '#CCC'
            },
            '& .editabletext input': {
                borderColor: 'black',
                borderStyle: 'solid'
            },
            '& .editabletext .copybox': {
                display: 'none'
            },
            '& .editabletext:hover .copybox': {
                display: 'block'
            },
        }}
        onResize={resizeGrid}
        processRowUpdate={(update: T, original: T) => {
            if (JSON.stringify(update) != JSON.stringify(original)) {
                void updateRow(update, original);
            }
            return update;
        }}
        onColumnWidthChange={async (params: GridColumnResizeParams) => {
            if (config) {
                let currConfig = { ...config };
                currConfig[params.colDef.field] = currConfig?.[params.colDef.field] ?? {};
                currConfig[params.colDef.field].width = params.width;
                void saveConfig(currConfig);

            }
        }} /></div>;
};
